import { DownOutlined, InfoCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Drawer, Dropdown, Flex, Form, Input, InputNumber, Modal, Popconfirm, Segmented, Select, Skeleton, Space, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useDeferredValue, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type Schemas, type StockDetail, type StockRow } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n, type MessageKey } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { useActiveWarehouse, useWarehouses } from "@/lib/useWarehouses";
import { ExportButton } from "@/components/list/ExportButton";
import { useListPage } from "@/components/list/useListPage";
import { PlatformLabel } from "../channels/PlatformBadge";
import { ConditionTag, conditionOptions, conditions, type Condition } from "./conditions";

type Movement = StockDetail["movements"][number];
type SoldOn = StockDetail["soldOn"][number];
type Hold = StockDetail["holds"][number];
type Level = StockDetail["levels"][number];
type Quantities = Pick<StockRow, "onHand" | "sellable" | "damaged" | "quarantine" | "allocated" | "allocatedOrder" | "allocatedOther" | "variance" | "available">;
type StockTarget = { skuId: string; code: string; name: string; warehouseId: string; q: Quantities };
type Action = { kind: "adjust" | "move" | "hold"; target: StockTarget };
const STOCK_STATES = ["", "low", "out", "damaged", "quarantine", "variance"] as const;
type StockState = (typeof STOCK_STATES)[number];
type TotalRow = Schemas["StockTotalRow"];
const VIEWS = ["sku", "warehouse"] as const;
type View = (typeof VIEWS)[number];
/** What is on its way, shown next to available. */
type Pending = Pick<StockRow, "low" | "incoming" | "incomingPo" | "toReturn"> & { noSerial?: number };

/**
 * Ringkasan stok (App Flow §8, Plan §6), the sales side of stock: per SKU over
 * every warehouse (a row opens to its warehouses), or per SKU and warehouse.
 * On hand split by condition, allocations split by kind, and the one
 * available number every store is told. Stock per bin is the warehouse's
 * screen (Stok per lokasi).
 */
export function Inventory() {
  const { t, formatNumber } = useI18n();
  const { can } = useAuth();
  const warehouses = useWarehouses();
  const [search, setSearch] = useUrlFilter<string>("q", "");
  const [picked, setWarehouseId] = useUrlFilter<string>("warehouse", "");
  // Members limited to some warehouses see one at a time, the active one first.
  const active = useActiveWarehouse().current?.id ?? "";
  const warehouseId = picked || (warehouses.restricted ? active : "");
  const [stock, setStock] = useUrlFilter<StockState>("stock", "", STOCK_STATES);
  const [locSearch, setLocSearch] = useUrlFilter<string>("location", "");
  const location = useDeferredValue(locSearch.trim().toUpperCase());
  const page = useListPage();
  // "skuId" or "skuId@warehouseId": one param, so opening sets it in one go.
  const [openSku, setOpenSku] = useUrlFilter<string>("sku", "");
  const [openSkuId, openWarehouse = ""] = openSku.split("@");
  const [action, setAction] = useState<Action | null>(null);
  const q = useDeferredValue(search.trim());
  const manage = can("inventory.manage");
  const multiWarehouse = warehouses.accessible.length > 1 || (!warehouses.restricted && warehouses.active.length > 1);
  // Over every warehouse needs them all; with one warehouse the views agree.
  const canTotal = multiWarehouse && !warehouses.restricted;
  const [viewRaw, setView] = useUrlFilter<View>("view", "sku", VIEWS);
  const view: View = canTotal ? viewRaw : "warehouse";

  const filters = { ...(q ? { q } : {}), ...(warehouseId ? { warehouseId } : {}), ...(stock ? { stock } : {}), ...(location ? { location } : {}) };
  const list = useQuery({
    queryKey: ["inventory", "list", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/inventory", { params: { query: { ...filters, ...page.query } } })),
    enabled: view === "warehouse",
    placeholderData: keepPreviousData,
  });
  const totalFilters = { ...(q ? { q } : {}), ...(stock ? { stock } : {}) };
  const totals = useQuery({
    queryKey: ["inventory", "totals", totalFilters, page.query],
    queryFn: () => unwrap(api.GET("/v1/inventory/totals", { params: { query: { ...totalFilters, ...page.query } } })),
    enabled: view === "sku",
    placeholderData: keepPreviousData,
  });
  const rows = list.data?.items ?? [];
  const lowStock = (view === "sku" ? totals.data?.lowStock : list.data?.lowStock) ?? 0;
  const num = (v: number) => <span className="z-num">{formatNumber(v)}</span>;
  const available = (v: number, r: Pending) => (
    <Space size={6}>
      {r.low && (
        <Tag color="warning" icon={<WarningOutlined />}>
          {t("inventory.low")}
        </Tag>
      )}
      <Typography.Text strong className="z-num">
        {formatNumber(v)}
      </Typography.Text>
      {(r.noSerial ?? 0) > 0 && (
        <Tooltip title={t("serial.noSerialHelp")}>
          <Tag color="warning" className="z-num">
            {t("serial.noSerial", { n: formatNumber(r.noSerial ?? 0) })}
          </Tag>
        </Tooltip>
      )}
      {r.incoming > 0 && (
        <Tooltip title={t("inventory.incomingHelp")}>
          <Tag color="blue" className="z-num">
            {t("inventory.incoming", { n: formatNumber(r.incoming) })}
          </Tag>
        </Tooltip>
      )}
      {r.incomingPo > 0 && (
        <Tooltip title={t("inventory.incomingPoHelp")}>
          <Tag color="geekblue" className="z-num">
            {t("inventory.incomingPo", { n: formatNumber(r.incomingPo) })}
          </Tag>
        </Tooltip>
      )}
      {r.toReturn > 0 && (
        <Tooltip title={t("inventory.toReturnHelp")}>
          <Tag color="purple" className="z-num">
            {t("inventory.toReturn", { n: formatNumber(r.toReturn) })}
          </Tag>
        </Tooltip>
      )}
    </Space>
  );
  const actions = (r: StockRow) => <ActionMenu target={{ skuId: r.skuId, code: r.code, name: r.name, warehouseId: r.warehouseId, q: r }} onPick={setAction} />;

  return (
    <>
      <Typography.Title level={2} style={{ marginTop: 0, marginBottom: 16, fontSize: 28 }}>
        {t("inventory.title")}
      </Typography.Title>

      <Flex gap={12} wrap style={{ marginBottom: 16 }} align="center">
        {canTotal && (
          <Segmented<View>
            value={view}
            onChange={setView}
            options={[
              { value: "sku", label: t("inventory.view.sku") },
              { value: "warehouse", label: t("inventory.view.warehouse") },
            ]}
          />
        )}
        <Input.Search allowClear placeholder={t("inventory.search")} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 300 }} />
        {view === "warehouse" && (
          <Input.Search
            allowClear
            placeholder={t("inventory.locationSearch")}
            value={locSearch}
            onChange={(e) => setLocSearch(e.target.value)}
            className="z-num"
            style={{ width: 220 }}
          />
        )}
        {view === "warehouse" && multiWarehouse && (
          <Select<string>
            value={warehouseId}
            onChange={setWarehouseId}
            style={{ width: 200 }}
            options={[
              ...(warehouses.restricted ? [] : [{ value: "", label: t("inventory.allWarehouses") }]),
              ...warehouses.accessible.map((w) => ({ value: w.id, label: w.name })),
            ]}
          />
        )}
        <Select<StockState>
          value={stock}
          onChange={setStock}
          style={{ width: 220 }}
          options={[
            { value: "", label: t("inventory.stock.all") },
            { value: "low", label: t("inventory.lowOnly", { n: lowStock }) },
            { value: "out", label: t("inventory.stock.out") },
            { value: "damaged", label: t("inventory.stock.damaged") },
            { value: "quarantine", label: t("inventory.stock.quarantine") },
            { value: "variance", label: t("inventory.stock.variance") },
          ]}
        />
        <div style={{ flex: 1 }} />
        <ExportButton
          path={view === "sku" ? "/v1/inventory/totals/export" : "/v1/inventory/export"}
          query={view === "sku" ? totalFilters : filters}
          filename={t("inventory.title")}
          columns={[
            { key: "code", label: t("catalog.code") },
            { key: "name", label: t("common.name") },
            view === "sku" ? { key: "warehouses", label: t("inventory.warehouses"), kind: "number" } : { key: "warehouseName", label: t("orders.warehouse") },
            { key: "onHand", label: t("inventory.onHand"), kind: "number" },
            { key: "sellable", label: t("inventory.sellable"), kind: "number" },
            { key: "damaged", label: t("inventory.damaged"), kind: "number" },
            { key: "quarantine", label: t("inventory.quarantine"), kind: "number" },
            { key: "allocatedOrder", label: t("inventory.allocatedOrder"), kind: "number" },
            { key: "allocatedOther", label: t("inventory.allocatedOther"), kind: "number" },
            { key: "variance", label: t("inventory.variance"), kind: "number" },
            { key: "available", label: t("inventory.available"), kind: "number" },
            { key: "incoming", label: t("inventory.incomingTitle"), kind: "number" },
            { key: "incomingPo", label: t("inventory.incomingPoTitle"), kind: "number" },
            { key: "toReturn", label: t("inventory.toReturnTitle"), kind: "number" },
            { key: "low", label: t("inventory.low"), values: { true: t("common.yes"), false: t("common.no") } },
          ]}
        />
      </Flex>

      {view === "sku" && (
        <Table<TotalRow>
          rowKey="skuId"
          loading={totals.isFetching && !totals.data}
          dataSource={totals.data?.items ?? []}
          pagination={page.pagination(totals.data)}
          scroll={{ x: 1250 }}
          locale={{ emptyText: t("inventory.empty") }}
          onRow={(r) => ({ onClick: () => setOpenSku(r.skuId), style: { cursor: "pointer" } })}
          expandable={{
            expandedRowRender: (r) => <PerWarehouse skuId={r.skuId} num={num} available={available} actions={manage ? actions : undefined} onOpen={(w) => setOpenSku(`${r.skuId}@${w}`)} />,
            rowExpandable: (r) => r.warehouses > 0,
          }}
          columns={[
            { title: t("catalog.code"), dataIndex: "code", width: 180, fixed: "left", render: (v: string) => <span className="z-num">{v}</span> },
            { title: t("common.name"), dataIndex: "name", width: 220 },
            {
              title: t("inventory.warehouses"),
              dataIndex: "warehouses",
              width: 90,
              align: "right",
              render: (v: number) => <span className="z-num">{formatNumber(v)}</span>,
            },
            ...quantityColumns<TotalRow>(t, num),
            { title: <AvailableTitle />, dataIndex: "available", width: 140, align: "right" as const, render: (v: number, r: TotalRow) => available(v, r) },
          ]}
        />
      )}

      {view === "warehouse" && (
      <Table<StockRow>
        rowKey={(r) => `${r.skuId}/${r.warehouseId}`}
        loading={list.isFetching && !list.data}
        dataSource={rows}
        pagination={page.pagination(list.data)}
        scroll={{ x: 1250 }}
        locale={{ emptyText: t("inventory.empty") }}
        onRow={(r) => ({ onClick: () => setOpenSku(`${r.skuId}@${r.warehouseId}`), style: { cursor: "pointer" } })}
        columns={[
          { title: t("catalog.code"), dataIndex: "code", width: 180, fixed: "left", render: (v: string) => <span className="z-num">{v}</span> },
          { title: t("common.name"), dataIndex: "name", width: 220 },
          ...(multiWarehouse ? [{ title: t("orders.warehouse"), dataIndex: "warehouseName", width: 150 }] : []),
          ...(location
            ? [
                {
                  title: t("inventory.atLocation"),
                  dataIndex: "atLocation",
                  width: 200,
                  render: (v: StockRow["atLocation"]) => (
                    <Space size={4} wrap>
                      {(v ?? []).map((x) => (
                        <Tag key={x.code} className="z-num" style={{ marginInlineEnd: 0 }}>
                          {x.code} · {formatNumber(x.quantity)}
                        </Tag>
                      ))}
                    </Space>
                  ),
                },
              ]
            : []),
          ...quantityColumns<StockRow>(t, num),
          { title: <AvailableTitle />, dataIndex: "available", width: 140, align: "right" as const, render: (v: number, r: StockRow) => available(v, r) },
          ...(manage ? [{ title: t("common.actions"), width: 130, render: (_: unknown, r: StockRow) => actions(r) }] : []),
        ]}
      />
      )}

      <StockDrawer
        skuId={openSkuId || null}
        warehouseId={openWarehouse || warehouseId}
        onWarehouse={(w) => setOpenSku(`${openSkuId}@${w}`)}
        onClose={() => setOpenSku("")}
        onAction={manage ? setAction : undefined}
      />
      <AdjustModal target={action?.kind === "adjust" ? action.target : null} onClose={() => setAction(null)} />
      <MoveModal target={action?.kind === "move" ? action.target : null} onClose={() => setAction(null)} />
      <HoldModal target={action?.kind === "hold" ? action.target : null} onClose={() => setAction(null)} />
    </>
  );
}

/** An SKU's row in Ringkasan stok, opened: its stock in each warehouse. */
function PerWarehouse({
  skuId,
  num,
  available,
  actions,
  onOpen,
}: {
  skuId: string;
  num: (v: number) => ReactNode;
  available: (v: number, r: Pending) => ReactNode;
  actions?: (r: StockRow) => ReactNode;
  onOpen: (warehouseId: string) => void;
}) {
  const { t } = useI18n();
  const rows = useQuery({
    queryKey: ["inventory", "list", { skuId }],
    queryFn: () => unwrap(api.GET("/v1/inventory", { params: { query: { skuId, limit: 200 } } })),
  });
  return (
    <Table<StockRow>
      size="small"
      rowKey="warehouseId"
      loading={rows.isPending}
      pagination={false}
      dataSource={rows.data?.items ?? []}
      onRow={(r) => ({ onClick: () => onOpen(r.warehouseId), style: { cursor: "pointer" } })}
      columns={[
        { title: t("orders.warehouse"), dataIndex: "warehouseName", width: 200 },
        ...quantityColumns<StockRow>(t, num),
        { title: t("inventory.available"), dataIndex: "available", width: 140, align: "right", render: (v: number, r: StockRow) => available(v, r) },
        ...(actions ? [{ width: 130, render: (_: unknown, r: StockRow) => actions(r) }] : []),
      ]}
    />
  );
}

/** The breakdown columns shared by the list and the per-warehouse table. */
function quantityColumns<T extends Quantities>(t: (k: MessageKey) => string, num: (v: number) => ReactNode): ColumnsType<T> {
  // Zero in the side buckets is the normal case: show it quietly.
  const quiet = (v: number) =>
    v === 0 ? (
      <Typography.Text type="secondary" className="z-num">
        0
      </Typography.Text>
    ) : (
      num(v)
    );
  return [
    { title: t("inventory.onHand"), dataIndex: "onHand", width: 100, align: "right", render: (v: number) => num(v) },
    { title: t("inventory.damaged"), dataIndex: "damaged", width: 90, align: "right", render: quiet },
    { title: t("inventory.quarantine"), dataIndex: "quarantine", width: 100, align: "right", render: quiet },
    { title: t("inventory.allocatedOrder"), dataIndex: "allocatedOrder", width: 120, align: "right", render: quiet },
    {
      title: (
        <Space size={4}>
          {t("inventory.allocatedOther")}
          <Tooltip title={t("inventory.allocatedOtherHelp")}>
            <InfoCircleOutlined style={{ color: "var(--z-muted)" }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: "allocatedOther",
      width: 130,
      align: "right",
      render: quiet,
    },
    {
      title: (
        <Space size={4}>
          {t("inventory.variance")}
          <Tooltip title={t("inventory.varianceHelp")}>
            <InfoCircleOutlined style={{ color: "var(--z-muted)" }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: "variance",
      width: 100,
      align: "right",
      // Missing (+) lowers available; found (−) is already on a shelf.
      render: (v: number) => (v === 0 ? quiet(v) : <Typography.Text type={v > 0 ? "warning" : undefined} className="z-num">{v > 0 ? `+${v}` : v}</Typography.Text>),
    },
  ];
}

function AvailableTitle() {
  const { t } = useI18n();
  return (
    <Space size={4}>
      {t("inventory.available")}
      <Tooltip title={t("inventory.formula")}>
        <InfoCircleOutlined style={{ color: "var(--z-muted)" }} />
      </Tooltip>
    </Space>
  );
}

function ActionMenu({ target, onPick }: { target: StockTarget; onPick: (a: Action) => void }) {
  const { t } = useI18n();
  const warehouses = useWarehouses();
  // Under WMS, counts and conditions change per bin, from the stock detail.
  const wms = warehouses.data?.items.find((w) => w.id === target.warehouseId)?.wmsEnabled ?? false;
  const perBin = wms ? { disabled: true, title: t("inventory.wmsHint") } : {};
  return (
    <Dropdown
      trigger={["click"]}
      menu={{
        items: [
          { key: "adjust", label: t("inventory.adjust"), ...perBin },
          { key: "move", label: t("inventory.move"), ...perBin },
          { key: "hold", label: t("inventory.hold"), disabled: target.q.available === 0 },
        ],
        onClick: ({ key, domEvent }) => {
          domEvent.stopPropagation();
          onPick({ kind: key as Action["kind"], target });
        },
      }}
    >
      <Button size="small" onClick={(e) => e.stopPropagation()}>
        <Space size={4}>
          {t("common.actions")}
          <DownOutlined style={{ fontSize: 10 }} />
        </Space>
      </Button>
    </Dropdown>
  );
}

/**
 * One SKU in one warehouse. The levels table sums up every warehouse and
 * switches between them; bins, holds, incoming, returns and movements are the
 * chosen warehouse's only.
 */
function StockDrawer({
  skuId,
  warehouseId,
  onWarehouse,
  onClose,
  onAction,
}: {
  skuId: string | null;
  warehouseId: string;
  onWarehouse: (id: string) => void;
  onClose: () => void;
  onAction?: (a: Action) => void;
}) {
  const { t, tDynamic, formatDate, formatDateTime, formatNumber } = useI18n();
  const { me, can } = useAuth();
  const navigate = useNavigate();
  const warehouses = useWarehouses();
  const tz = me?.workspace?.timezone;
  const release = useStockMutation((id: string) => unwrap(api.POST("/v1/inventory/holds/{holdID}/release", { params: { path: { holdID: id } } })), "inventory.released");

  // Without a warehouse (a link from the catalogue), the first one listed.
  const levelsOf = useQuery({
    queryKey: ["inventory", "sku", skuId, ""],
    queryFn: () => unwrap(api.GET("/v1/inventory/skus/{skuID}", { params: { path: { skuID: skuId! } } })),
    enabled: skuId !== null && !warehouseId,
  });
  const wid = warehouseId || levelsOf.data?.levels[0]?.warehouseId || "";
  const detail = useQuery({
    queryKey: ["inventory", "sku", skuId, wid],
    queryFn: () => unwrap(api.GET("/v1/inventory/skus/{skuID}", { params: { path: { skuID: skuId! }, query: { warehouseId: wid } } })),
    enabled: skuId !== null && wid !== "",
  });
  const d = detail.data ?? (warehouseId ? undefined : levelsOf.data);
  const num = (v: number) => <span className="z-num">{formatNumber(v)}</span>;
  const multi = (d?.levels.length ?? 0) > 1;
  const whName = d?.levels.find((l) => l.warehouseId === wid)?.warehouseName ?? warehouses.nameOf(wid);

  return (
    <Drawer open={skuId !== null} onClose={onClose} size="large" title={d ? `${d.sku.code} · ${d.sku.name}${multi && whName ? ` · ${whName}` : ""}` : t("inventory.title")} destroyOnHidden>
      {!d ? (
        <Skeleton active />
      ) : (
        <Space orientation="vertical" size={24} style={{ width: "100%" }}>
          <div>
            <Typography.Title level={3} style={{ fontSize: 16 }}>
              {t("inventory.levels")}
            </Typography.Title>
            <Table<Level>
              size="small"
              rowKey="warehouseId"
              pagination={false}
              dataSource={d.levels}
              rowSelection={
                multi
                  ? { type: "radio", selectedRowKeys: [wid], onChange: (keys) => keys[0] && onWarehouse(String(keys[0])), columnWidth: 40 }
                  : undefined
              }
              onRow={(l) => (multi ? { onClick: () => onWarehouse(l.warehouseId), style: { cursor: "pointer" } } : {})}
              scroll={{ x: 820 }}
              columns={[
                { title: t("orders.warehouse"), dataIndex: "warehouseName", width: 140, render: (v: string | undefined, l) => v ?? warehouses.nameOf(l.warehouseId) ?? "—" },
                ...quantityColumns<Level>(t, num),
                {
                  title: <AvailableTitle />,
                  dataIndex: "available",
                  width: 110,
                  align: "right",
                  render: (v: number) => (
                    <Typography.Text strong className="z-num">
                      {formatNumber(v)}
                    </Typography.Text>
                  ),
                },
                ...(onAction
                  ? [
                      {
                        width: 120,
                        render: (_: unknown, l: Level) => <ActionMenu target={{ skuId: d.sku.id, code: d.sku.code, name: d.sku.name, warehouseId: l.warehouseId, q: l }} onPick={onAction} />,
                      },
                    ]
                  : []),
              ]}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {multi ? `${t("inventory.pickWarehouse")} ` : ""}
              {t("inventory.formula")}
            </Typography.Text>
          </div>

          {d.levels
            .filter((l) => l.bins)
            .map((l) => (
              <BinTable key={l.warehouseId} sku={d.sku} level={l} manage={!!onAction} />
            ))}

          {d.incoming.length > 0 && (
            <div>
              <Typography.Title level={3} style={{ fontSize: 16 }}>
                {t("inventory.incomingTitle")}
              </Typography.Title>
              <Table
                size="small"
                rowKey={(x) => `${x.transferId}-${x.warehouseId}`}
                pagination={false}
                dataSource={d.incoming}
                columns={[
                  {
                    title: t("wms.in.number"),
                    dataIndex: "number",
                    width: 130,
                    render: (v: string, x) => (
                      <Typography.Link className="z-num" onClick={() => navigate(`/app/warehouse/transfers/${x.transferId}`)}>
                        {v}
                      </Typography.Link>
                    ),
                  },
                  { title: t("wms.tr.route"), render: (_, x) => `${x.fromName} → ${x.warehouseName}` },
                  { title: t("inventory.quantity"), dataIndex: "quantity", width: 80, align: "right" as const, render: num },
                  { title: t("wms.tr.shipped"), dataIndex: "shippedAt", width: 170, render: (v: string | null) => (v ? formatDateTime(v, tz) : "—") },
                ]}
              />
            </div>
          )}

          {d.incomingPurchases.length > 0 && (
            <div>
              <Typography.Title level={3} style={{ fontSize: 16 }}>
                {t("inventory.incomingPoTitle")}
              </Typography.Title>
              <Table
                size="small"
                rowKey={(x) => `${x.purchaseOrderId}-${x.warehouseId}`}
                pagination={false}
                dataSource={d.incomingPurchases}
                columns={[
                  {
                    title: t("po.number"),
                    dataIndex: "number",
                    width: 130,
                    render: (v: string, x) =>
                      can("procurement.view") ? (
                        <Typography.Link className="z-num" onClick={() => navigate(`/app/procurement/orders/${x.purchaseOrderId}`)}>
                          {v}
                        </Typography.Link>
                      ) : (
                        <span className="z-num">{v}</span>
                      ),
                  },
                  { title: t("po.supplier"), render: (_, x) => `${x.supplierName} → ${x.warehouseName}` },
                  { title: t("inventory.quantity"), dataIndex: "quantity", width: 80, align: "right" as const, render: num },
                  { title: t("po.expectedDate"), dataIndex: "expectedDate", width: 130, render: (v: string | null) => (v ? formatDate(v) : "—") },
                ]}
              />
            </div>
          )}

          {d.toReturn.length > 0 && (
            <div>
              <Typography.Title level={3} style={{ fontSize: 16 }}>
                {t("inventory.toReturnTitle")}
              </Typography.Title>
              <Table
                size="small"
                rowKey={(x) => `${x.caseId}-${x.warehouseId}`}
                pagination={false}
                dataSource={d.toReturn}
                columns={[
                  {
                    title: t("as.number"),
                    dataIndex: "number",
                    width: 130,
                    render: (v: string, x) => (
                      <Typography.Link className="z-num" onClick={() => navigate(`/app/aftersales/${x.caseId}`)}>
                        {v}
                      </Typography.Link>
                    ),
                  },
                  { title: t("as.warehouse"), dataIndex: "warehouseName" },
                  { title: t("as.returnTracking"), dataIndex: "trackingNo", render: (v: string) => (v ? <span className="z-num">{v}</span> : "—") },
                  { title: t("inventory.quantity"), dataIndex: "quantity", width: 80, align: "right" as const, render: num },
                ]}
              />
            </div>
          )}

          <div>
            <Typography.Title level={3} style={{ fontSize: 16 }}>
              {t("inventory.holds")}
            </Typography.Title>
            <Table<Hold>
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={d.holds}
              locale={{ emptyText: t("inventory.holdsEmpty") }}
              columns={[
                { title: t("inventory.holdReason"), dataIndex: "reason", width: 140, render: (v: string) => <Tag>{tDynamic(`inventory.holdReason.${v}`, "inventory.holdReason.hold")}</Tag> },
                ...(multi ? [{ title: t("orders.warehouse"), dataIndex: "warehouseName" }] : []),
                { title: t("inventory.quantity"), dataIndex: "quantity", width: 80, align: "right" as const, render: num },
                { title: t("inventory.note"), dataIndex: "note" },
                {
                  title: t("inventory.since"),
                  dataIndex: "createdAt",
                  width: 170,
                  render: (v: string, h: Hold) => (
                    <Space orientation="vertical" size={0}>
                      <span>{formatDateTime(v, tz)}</span>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {h.actorName}
                      </Typography.Text>
                    </Space>
                  ),
                },
                ...(onAction
                  ? [
                      {
                        width: 80,
                        render: (_: unknown, h: Hold) =>
                          h.reason === "hold" && (
                            <Popconfirm
                              title={t("inventory.releaseConfirm", { n: h.quantity })}
                              okText={t("inventory.release")}
                              cancelText={t("common.cancel")}
                              onConfirm={() => release.mutateAsync(h.id)}
                            >
                              <Button size="small">{t("inventory.release")}</Button>
                            </Popconfirm>
                          ),
                      },
                    ]
                  : []),
              ]}
            />
          </div>

          <div>
            <Typography.Title level={3} style={{ fontSize: 16 }}>
              {t("inventory.soldOn")}
            </Typography.Title>
            <Table<SoldOn>
              size="small"
              rowKey={(s) => `${s.connectionId}/${s.sellerSku}/${s.variant}`}
              pagination={false}
              dataSource={d.soldOn}
              locale={{ emptyText: t("inventory.soldOnEmpty") }}
              columns={[
                {
                  title: t("orders.store"),
                  render: (_, s) => (
                    <Space orientation="vertical" size={0}>
                      <PlatformLabel platform={s.platform} />
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {s.shopName}
                        {s.variant ? ` · ${s.variant}` : ""}
                      </Typography.Text>
                    </Space>
                  ),
                },
                {
                  title: t("inventory.platformStock"),
                  dataIndex: "platformStock",
                  align: "right",
                  render: (v: number | null) => <span className="z-num">{v === null ? "—" : formatNumber(v)}</span>,
                },
                {
                  title: t("inventory.lastPushed"),
                  render: (_, s) =>
                    s.lastPushError ? (
                      <Tooltip title={s.lastPushError}>
                        <Tag color="error">{t("inventory.pushError")}</Tag>
                      </Tooltip>
                    ) : s.lastPushedAt ? (
                      <Space orientation="vertical" size={0}>
                        <span className="z-num">{formatNumber(s.lastPushedQty ?? 0)}</span>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {formatDateTime(s.lastPushedAt, tz)}
                        </Typography.Text>
                      </Space>
                    ) : (
                      t("common.never")
                    ),
                },
                {
                  title: t("inventory.mapping"),
                  dataIndex: "mappingSource",
                  render: (v: string | null) => (v ? <Tag>{tDynamic(`inventory.mapping.${v}`, "inventory.mapping.manual")}</Tag> : "—"),
                },
              ]}
            />
          </div>

          <div>
            <Typography.Title level={3} style={{ fontSize: 16 }}>
              {t("inventory.movements")}
            </Typography.Title>
            <Table<Movement>
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={d.movements}
              locale={{ emptyText: t("inventory.movementsEmpty") }}
              columns={[
                { title: t("logs.time"), dataIndex: "at", width: 160, render: (v: string) => formatDateTime(v, tz) },
                {
                  title: t("inventory.delta"),
                  dataIndex: "delta",
                  width: 90,
                  align: "right",
                  render: (v: number) => (
                    <Typography.Text strong className="z-num" type={v < 0 ? "danger" : "success"}>
                      {v > 0 ? `+${formatNumber(v)}` : formatNumber(v)}
                    </Typography.Text>
                  ),
                },
                { title: t("inventory.condition"), dataIndex: "condition", width: 110, render: (v: Condition) => <ConditionTag condition={v} /> },
                {
                  title: t("inventory.reason"),
                  render: (_, m) => (
                    <Space orientation="vertical" size={0}>
                      <span>{tDynamic(`inventory.reason.${m.reason}`, "inventory.reason.adjustment")}</span>
                      {m.note && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {m.note}
                        </Typography.Text>
                      )}
                    </Space>
                  ),
                },
                ...(multi ? [{ title: t("orders.warehouse"), dataIndex: "warehouseId", render: (v: string) => warehouses.nameOf(v) ?? "—" }] : []),
                { title: t("logs.actor"), dataIndex: "actorName" },
              ]}
            />
          </div>
        </Space>
      )}
    </Drawer>
  );
}

/** A stock change: success toast, then every stock number on screen is refetched. */
function useStockMutation<V>(fn: (v: V) => Promise<unknown>, done: MessageKey, after?: () => void, onFieldError?: (err: unknown) => boolean) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const errorText = useErrorText();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void message.success(t(done));
      after?.();
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (err) => {
      if (!onFieldError?.(err)) void message.error(errorText(err));
    },
  });
}

function TargetHeader({ target, body }: { target: StockTarget; body: MessageKey }) {
  const { t } = useI18n();
  const warehouses = useWarehouses();
  return (
    <>
      <Typography.Paragraph type="secondary">{t(body)}</Typography.Paragraph>
      <Typography.Paragraph>
        {target.name}
        {warehouses.active.length > 1 && <Typography.Text type="secondary"> · {warehouses.nameOf(target.warehouseId)}</Typography.Text>}
      </Typography.Paragraph>
    </>
  );
}

function noteRules(t: (k: MessageKey) => string) {
  return [
    { required: true, whitespace: true, message: t("field.required") },
    { max: 300, message: t("field.too_long") },
  ];
}

type AdjustValues = { condition: Condition; quantity: number; reason: string };

function AdjustModal({ target, onClose }: { target: StockTarget | null; onClose: () => void }) {
  const { t, formatNumber } = useI18n();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<AdjustValues>();
  const condition = Form.useWatch("condition", form) ?? "sellable";
  const save = useStockMutation(
    (v: AdjustValues) =>
      unwrap(api.POST("/v1/inventory/adjustments", { body: { skuId: target!.skuId, warehouseId: target!.warehouseId, condition: v.condition, quantity: v.quantity, reason: v.reason.trim() } })),
    "inventory.adjusted",
    onClose,
    (err) => applyFieldErrors(form, err),
  );

  return (
    <Modal
      open={target !== null}
      title={target ? `${t("inventory.adjust")} · ${target.code}` : t("inventory.adjust")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {target && (
        <>
          <TargetHeader target={target} body="inventory.adjustBody" />
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            initialValues={{ condition: "sellable", quantity: target.q.sellable, reason: "" }}
            onValuesChange={(changed: Partial<AdjustValues>) => {
              // Start from the current count of the newly picked condition.
              if (changed.condition) form.setFieldValue("quantity", target.q[changed.condition]);
            }}
            onFinish={(v) => save.mutate(v)}
          >
            <Flex gap={16}>
              <Form.Item name="condition" label={t("inventory.condition")} style={{ width: 180 }}>
                <Select<Condition> options={conditionOptions(t)} />
              </Form.Item>
              <Form.Item name="quantity" label={t("inventory.newQty")} extra={t("inventory.current", { n: formatNumber(target.q[condition]) })} rules={[{ required: true, message: t("field.required") }]}>
                <InputNumber min={0} max={10_000_000} precision={0} autoFocus style={{ width: 180 }} />
              </Form.Item>
            </Flex>
            <Form.Item name="reason" label={t("inventory.reason")} rules={noteRules(t)}>
              <Input.TextArea rows={2} placeholder={t("inventory.reasonPlaceholder")} maxLength={300} />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}

type MoveValues = { from: Condition; to: Condition; quantity: number; note: string };

function MoveModal({ target, onClose }: { target: StockTarget | null; onClose: () => void }) {
  const { t, formatNumber } = useI18n();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<MoveValues>();
  const from = Form.useWatch("from", form) ?? "sellable";
  const save = useStockMutation(
    (v: MoveValues) =>
      unwrap(api.POST("/v1/inventory/moves", { body: { skuId: target!.skuId, warehouseId: target!.warehouseId, from: v.from, to: v.to, quantity: v.quantity, note: v.note.trim() } })),
    "inventory.moved",
    onClose,
    (err) => applyFieldErrors(form, err),
  );
  const max = target ? target.q[from] : 0;

  return (
    <Modal
      open={target !== null}
      title={target ? `${t("inventory.move")} · ${target.code}` : t("inventory.move")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {target && (
        <>
          <TargetHeader target={target} body="inventory.moveBody" />
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            initialValues={{ from: "sellable", to: "damaged", quantity: 1, note: "" }}
            onValuesChange={(changed: Partial<MoveValues>) => {
              // Keep the two ends different: picking the other end's condition moves that end aside.
              const v = form.getFieldsValue();
              if (changed.from && changed.from === v.to) form.setFieldValue("to", conditions.find((c) => c !== changed.from));
              if (changed.to && changed.to === v.from) form.setFieldValue("from", conditions.find((c) => c !== changed.to));
            }}
            onFinish={(v) => save.mutate(v)}
          >
            <Flex gap={16}>
              <Form.Item name="from" label={t("inventory.from")} extra={t("inventory.current", { n: formatNumber(max) })} style={{ flex: 1 }}>
                <Select<Condition> options={conditionOptions(t)} />
              </Form.Item>
              <Form.Item name="to" label={t("inventory.to")} style={{ flex: 1 }}>
                <Select<Condition> options={conditionOptions(t)} />
              </Form.Item>
              <Form.Item name="quantity" label={t("inventory.quantity")} rules={[{ required: true, message: t("field.required") }]} style={{ width: 120 }}>
                <InputNumber min={1} max={Math.max(1, max)} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Flex>
            <Form.Item name="note" label={t("inventory.note")} rules={noteRules(t)}>
              <Input.TextArea rows={2} placeholder={t("inventory.moveNotePlaceholder")} maxLength={300} />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}

type HoldValues = { quantity: number; note: string };

function HoldModal({ target, onClose }: { target: StockTarget | null; onClose: () => void }) {
  const { t, formatNumber } = useI18n();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<HoldValues>();
  const save = useStockMutation(
    (v: HoldValues) => unwrap(api.POST("/v1/inventory/holds", { body: { skuId: target!.skuId, warehouseId: target!.warehouseId, quantity: v.quantity, note: v.note.trim() } })),
    "inventory.held",
    onClose,
    (err) => applyFieldErrors(form, err),
  );

  return (
    <Modal
      open={target !== null}
      title={target ? `${t("inventory.hold")} · ${target.code}` : t("inventory.hold")}
      okText={t("inventory.hold")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {target && (
        <>
          <TargetHeader target={target} body="inventory.holdBody" />
          <Form form={form} layout="vertical" requiredMark={false} initialValues={{ quantity: 1, note: "" }} onFinish={(v) => save.mutate(v)}>
            <Form.Item
              name="quantity"
              label={t("inventory.quantity")}
              extra={t("inventory.holdMax", { n: formatNumber(target.q.available) })}
              rules={[{ required: true, message: t("field.required") }]}
            >
              <InputNumber min={1} max={Math.max(1, target.q.available)} precision={0} autoFocus style={{ width: 180 }} />
            </Form.Item>
            <Form.Item name="note" label={t("inventory.note")} rules={noteRules(t)}>
              <Input.TextArea rows={2} placeholder={t("inventory.holdNotePlaceholder")} maxLength={300} />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}

type Bin = NonNullable<Level["bins"]>[number];

/** Stock per location of one WMS warehouse, with per-bin count and move. */
function BinTable({ sku, level, manage }: { sku: StockDetail["sku"]; level: Level; manage: boolean }) {
  const { t, formatNumber, formatDate } = useI18n();
  const tz = useAuth().me?.workspace?.timezone;
  const [counting, setCounting] = useState<Bin | null>(null);
  const [moving, setMoving] = useState<Bin | null>(null);
  return (
    <div>
      <Typography.Title level={3} style={{ fontSize: 16 }}>
        {t("inventory.bins")} · {level.warehouseName}
      </Typography.Title>
      <Table<Bin>
        size="small"
        rowKey={(b) => `${b.locationId}/${b.lotId ?? ""}/${b.condition}`}
        pagination={false}
        dataSource={level.bins ?? []}
        columns={[
          {
            title: t("inventory.bin.location"),
            dataIndex: "locationCode",
            render: (v: string, b) => (
              <Space size={4}>
                <Typography.Text strong className="z-num">
                  {v}
                </Typography.Text>
                {b.counting && <Tag color="warning">{t("wms.count.frozen")}</Tag>}
              </Space>
            ),
          },
          { title: t("inventory.bin.lot"), dataIndex: "lotNo", render: (v: string | null) => v ?? "—" },
          { title: t("inventory.bin.expires"), dataIndex: "expiresOn", render: (v: string | null) => <span className="z-num">{v ?? "—"}</span> },
          { title: t("inventory.condition"), dataIndex: "condition", render: (v: Condition) => <ConditionTag condition={v} /> },
          { title: t("wms.loc.units"), dataIndex: "quantity", align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
          { title: t("inventory.bin.reserved"), dataIndex: "reserved", align: "right", render: (v: number) => <span className="z-num">{v ? formatNumber(v) : "—"}</span> },
          {
            title: t("bins.receivedAt"),
            dataIndex: "receivedAt",
            render: (v: string | undefined) => <span className="z-num">{v ? formatDate(v, tz) : "—"}</span>,
          },
          ...(manage
            ? [
                {
                  width: 150,
                  // Counted and variance locations change through counts only.
                  render: (_: unknown, b: Bin) =>
                    !b.counting &&
                    b.locationKind !== "variance" && (
                      <Space>
                        <Button size="small" onClick={() => setCounting(b)}>
                          {t("inventory.bin.adjust")}
                        </Button>
                        <Button size="small" onClick={() => setMoving(b)}>
                          {t("inventory.bin.move")}
                        </Button>
                      </Space>
                    ),
                },
              ]
            : []),
        ]}
      />
      <BinCountModal sku={sku} warehouseId={level.warehouseId} bin={counting} onClose={() => setCounting(null)} />
      <BinMoveModal sku={sku} warehouseId={level.warehouseId} bin={moving} onClose={() => setMoving(null)} />
    </div>
  );
}

export function BinCountModal({ sku, warehouseId, bin, onClose }: { sku: StockDetail["sku"]; warehouseId: string; bin: Bin | null; onClose: () => void }) {
  const { t } = useI18n();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<{ quantity: number; reason: string }>();
  const save = useStockMutation(
    (v: { quantity: number; reason: string }) =>
      unwrap(
        api.POST("/v1/inventory/bins/adjust", {
          body: { skuId: sku.id, warehouseId, locationId: bin!.locationId, lotId: bin!.lotId, condition: bin!.condition, quantity: v.quantity, reason: v.reason.trim() },
        }),
      ),
    "inventory.bin.saved",
    onClose,
    (err) => applyFieldErrors(form, err),
  );
  return (
    <Modal
      open={bin !== null}
      title={bin ? t("inventory.bin.adjustTitle", { code: sku.code, location: bin.locationCode }) : ""}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {bin && (
        <Form form={form} layout="vertical" requiredMark={false} initialValues={{ quantity: bin.quantity, reason: "" }} onFinish={(v) => save.mutate(v)}>
          <Form.Item name="quantity" label={t("inventory.newQty")} extra={t("inventory.current", { n: bin.quantity })} rules={[{ required: true, message: t("field.required") }]}>
            <InputNumber min={bin.reserved} max={10_000_000} precision={0} autoFocus style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="reason" label={t("inventory.reason")} rules={noteRules(t)}>
            <Input.TextArea rows={2} placeholder={t("inventory.reasonPlaceholder")} maxLength={300} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}

type BinMoveValues = { toLocationId: string; toCondition: Condition; quantity: number; note: string };

export function BinMoveModal({ sku, warehouseId, bin, onClose }: { sku: StockDetail["sku"]; warehouseId: string; bin: Bin | null; onClose: () => void }) {
  const { t } = useI18n();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<BinMoveValues>();
  const locs = useQuery({
    queryKey: ["locations", warehouseId, "all"],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/locations", { params: { path: { warehouseID: warehouseId }, query: { status: "active" } } })),
    enabled: bin !== null,
  });
  const save = useStockMutation(
    (v: BinMoveValues) =>
      unwrap(
        api.POST("/v1/inventory/bins/move", {
          body: {
            skuId: sku.id,
            warehouseId,
            fromLocationId: bin!.locationId,
            lotId: bin!.lotId,
            condition: bin!.condition,
            toLocationId: v.toLocationId,
            toCondition: v.toCondition,
            quantity: v.quantity,
            note: v.note?.trim() ?? "",
          },
        }),
      ),
    "inventory.bin.saved",
    onClose,
    (err) => applyFieldErrors(form, err),
  );
  const free = bin ? bin.quantity - bin.reserved : 0;
  return (
    <Modal
      open={bin !== null}
      title={bin ? t("inventory.bin.moveTitle", { code: sku.code, location: bin.locationCode }) : ""}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {bin && (
        <Form form={form} layout="vertical" requiredMark={false} initialValues={{ toCondition: bin.condition, quantity: Math.min(1, free), note: "" }} onFinish={(v) => save.mutate(v)}>
          <Flex gap={12}>
            <Form.Item name="toLocationId" label={t("inventory.bin.toLocation")} style={{ flex: 1 }} rules={[{ required: true, message: t("field.required") }]}>
              <Select
                showSearch={{ optionFilterProp: "label" }}
                loading={locs.isPending}
                options={(locs.data?.items ?? []).filter((l) => l.id !== bin.locationId && l.kind !== "variance").map((l) => ({ value: l.id, label: l.code }))}
              />
            </Form.Item>
            <Form.Item name="toCondition" label={t("inventory.bin.toCondition")} style={{ width: 160 }}>
              <Select<Condition> options={conditionOptions(t)} />
            </Form.Item>
            <Form.Item name="quantity" label={t("inventory.quantity")} style={{ width: 110 }} rules={[{ required: true, message: t("field.required") }]}>
              <InputNumber min={1} max={Math.max(1, free)} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Flex>
          <Form.Item name="note" label={t("inventory.note")}>
            <Input.TextArea rows={2} maxLength={300} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}
